/**
 * Iris guidance — free-form behavioral rules that steer how the AI service
 * agent acts ("always confirm the address before booking", "never quote an
 * exact price over SMS"). Distinct from the knowledge base (facts): guidance is
 * about behavior. Proxied through operations at `/api/messaging/iris/guidance`
 * (operations is the FE's auth boundary + RBAC gate; it forwards to
 * ai-operations with the shared service key). Powers Settings → Iris → Train.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPost, apiPut, operationsClient } from '@/lib/api';

const BASE = '/api/messaging/iris/guidance';

/** Max length of a single guidance rule (matches the backend validator). */
export const GUIDANCE_MAX_LENGTH = 2500;

// Must match the backend GuidanceCategory enum (ai_ops schema) exactly.
export const GUIDANCE_CATEGORIES = [
  'TONE',
  'CLARIFICATION',
  'CONTENT',
  'ESCALATION',
  'SPAM',
  'OTHER',
] as const;
export type GuidanceCategory = (typeof GUIDANCE_CATEGORIES)[number];

export const GUIDANCE_CATEGORY_LABELS: Record<GuidanceCategory, string> = {
  TONE: 'Tone & style',
  CLARIFICATION: 'Clarifying questions',
  CONTENT: 'Answers & content',
  ESCALATION: 'Escalation & handoff',
  SPAM: 'Spam handling',
  OTHER: 'General',
};

export interface GuidanceRule {
  id: string;
  category: GuidanceCategory;
  text: string;
  enabled: boolean;
  sortOrder: number;
}

export interface CreateGuidancePayload {
  category: GuidanceCategory;
  text: string;
  enabled?: boolean;
}

export interface UpdateGuidancePayload {
  category?: GuidanceCategory;
  text?: string;
  enabled?: boolean;
}

/** Payload for the guidance AI-linter ("Optimize"). */
export interface LintGuidancePayload {
  text: string;
  category?: GuidanceCategory;
}

export type GuidanceLintIssueType = 'ambiguity' | 'redundancy' | 'contradiction' | 'unfollowable';

/** A single problem the linter found with a draft rule. */
export interface GuidanceLintIssue {
  type: GuidanceLintIssueType;
  detail: string;
}

/**
 * Result of the guidance AI-linter. `issues` lists problems found with the
 * draft rule (ambiguity, redundancy, contradiction with existing rules,
 * instructions the agent can't follow); `suggestion` is a reworded rewrite the
 * operator can adopt. `ok` is true when the rule is already clean.
 */
export interface LintGuidanceResult {
  ok: boolean;
  issues: GuidanceLintIssue[];
  suggestion: string | null;
}

export const irisGuidanceKey = ['messaging', 'iris-guidance'] as const;

export function useGuidance() {
  return useQuery({
    queryKey: irisGuidanceKey,
    queryFn: () => apiGet<GuidanceRule[]>(operationsClient, BASE),
    staleTime: 30_000,
  });
}

export function useCreateGuidance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateGuidancePayload) =>
      apiPost<GuidanceRule, CreateGuidancePayload>(operationsClient, BASE, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: irisGuidanceKey });
    },
  });
}

export function useUpdateGuidance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: { id: string } & UpdateGuidancePayload) =>
      apiPut<GuidanceRule, UpdateGuidancePayload>(
        operationsClient,
        `${BASE}/${encodeURIComponent(id)}`,
        payload,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: irisGuidanceKey });
    },
  });
}

/**
 * AI-linter for a draft guidance rule ("Optimize"). Reviews the text for
 * ambiguity, redundancy, contradiction with existing rules, and instructions
 * the agent can't follow, returning any issues plus a suggested rewrite.
 * Read-only — it never persists anything.
 */
export function useLintGuidance() {
  return useMutation({
    mutationFn: (payload: LintGuidancePayload) =>
      apiPost<LintGuidanceResult, LintGuidancePayload>(operationsClient, `${BASE}/lint`, payload),
  });
}

export function useDeleteGuidance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiDelete<void>(operationsClient, `${BASE}/${encodeURIComponent(id)}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: irisGuidanceKey });
    },
  });
}
